<template>
  <MultiSelect
    :model-value="modelValue"
    :options="tree.flat"
    option-label="label"
    option-value="value"
    :data-key="dataKey"
    :loading="loading"
    :show-toggle-all="false"
    :filter="sources.length > filterThreshold"
    :filter-placeholder="t('spmTableApi.dataSourceSelector.search')"
    @update:model-value="onUpdate"
    @hide="$emit('hide')"
  >
    <template #header>
      <div class="spm-datasource-header">
        <button
          type="button"
          class="spm-datasource-all-btn"
          :class="{ active: isAllSelected }"
          data-test="data-source-all"
          @click.stop="selectAll"
        >
          <i class="far fa-layer-group" />
          {{ t('spmTableApi.dataSourceSelector.allSources') }}
        </button>
      </div>
    </template>

    <template #option="{ option }">
      <div
        v-if="option.hasChildren"
        class="spm-ds-parent"
        :class="{ 'spm-ds--inactive': !option.active }"
      >
        <i class="far fa-folder-tree spm-ds-parent__icon" />
        <span class="spm-ds-parent__label">{{ option.shortLabel }}</span>
        <span class="spm-ds-parent__count">({{ getChildCount(option.value) }})</span>
        <span
          v-if="!option.active"
          class="spm-ds-inactive"
        >{{ t('spmTableApi.dataSourceSelector.inactive') }}</span>
      </div>
      <div
        v-else-if="option.isChild"
        class="spm-ds-child"
        :class="{ 'spm-ds--inactive': !option.active }"
      >
        <i class="far fa-store spm-ds-child__icon" />
        <span>{{ option.shortLabel }}</span>
        <span
          v-if="!option.active"
          class="spm-ds-inactive"
        >{{ t('spmTableApi.dataSourceSelector.inactive') }}</span>
      </div>
      <div
        v-else
        class="spm-ds-standalone"
        :class="{ 'spm-ds--inactive': !option.active }"
      >
        <i class="far fa-database spm-ds-standalone__icon" />
        <span>{{ option.shortLabel }}</span>
        <span
          v-if="!option.active"
          class="spm-ds-inactive"
        >{{ t('spmTableApi.dataSourceSelector.inactive') }}</span>
      </div>
    </template>

    <template #value>
      <span class="spm-datasource-value">
        <i :class="valueIcon" />
        {{ valueLabel }}
      </span>
    </template>
  </MultiSelect>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import MultiSelect from 'primevue/multiselect';
import {
  buildDataSourceTree,
  DataSourceOption,
  syncDataSourceSelection,
} from '@/composables/data-sources/dataSourceTree';

/**
 * Liste des points de vente : une ligne par niveau (parent avec le nombre de
 * ses magasins, magasins en retrait, source seule), sélection en cascade et
 * bouton « tous les points de vente ». Partagée par le sélecteur du header et
 * le filtre « Points de vente » des pages de statistiques, qui gardent chacun
 * leur façon d'enregistrer la sélection.
 */
export default defineComponent({
  name: 'DataSourceTreeSelect',

  components: { MultiSelect },

  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => [],
    },

    sources: {
      type: Array as PropType<DataSourceOption[]>,
      default: () => [],
    },

    // Identifie une option dans la liste : deux points de vente peuvent porter
    // le même nom, leur identifiant est unique.
    dataKey: {
      type: String,
      default: 'value',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    filterThreshold: {
      type: Number,
      default: 6,
    },
  },

  emits: ['update:modelValue', 'hide'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const tree = computed(() => buildDataSourceTree(props.sources));

    const isAllSelected = computed(() => props.sources.length > 0
      && props.modelValue.length === props.sources.length);

    const valueLabel = computed(() => {
      if (isAllSelected.value || props.modelValue.length === 0) {
        return t('spmTableApi.dataSourceSelector.allSources');
      }
      if (props.modelValue.length === 1) {
        const source = tree.value.items.find((s) => s.value === props.modelValue[0]);
        return source ? source.shortLabel : '1 source';
      }
      return t('spmTableApi.dataSourceSelector.nSelected', { count: props.modelValue.length });
    });

    const valueIcon = computed(() => {
      if (isAllSelected.value || props.modelValue.length === 0) return 'far fa-layer-group';
      if (props.modelValue.length === 1) {
        const source = tree.value.items.find((s) => s.value === props.modelValue[0]);
        return source?.isChild ? 'far fa-store' : 'far fa-database';
      }
      return 'far fa-database';
    });

    const onUpdate = (next: number[] | null) => {
      emit('update:modelValue', syncDataSourceSelection(
        props.modelValue,
        next ?? [],
        tree.value.childrenMap,
      ));
    };

    const selectAll = () => {
      emit('update:modelValue', props.sources.map((s) => s.value));
    };

    const getChildCount = (parentId: number): number => tree.value.childrenMap.get(parentId)?.length || 0;

    return {
      t,
      tree,
      isAllSelected,
      valueLabel,
      valueIcon,
      onUpdate,
      selectAll,
      getChildCount,
    };
  },
});
</script>

<style scoped>
.spm-datasource-value {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  white-space: nowrap;
  color: #6c757d;
}

.spm-datasource-value i {
  color: var(--text-color-secondary, #6b7280);
  font-size: 0.75rem;
}

.spm-datasource-header {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--surface-border, #dee2e6);
}

.spm-datasource-all-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-color, #495057);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s;
}

.spm-datasource-all-btn:hover {
  background: var(--surface-hover, #f8f9fa);
}

.spm-datasource-all-btn.active {
  background: #94c748;
  color: white;
  font-weight: 600;
}

.spm-ds-parent {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.spm-ds-parent__icon {
  color: #7c3aed;
  font-size: 0.8rem;
}

.spm-ds-parent__label {
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--text-color, #374151);
}

.spm-ds-parent__count {
  font-size: 0.7rem;
  color: var(--text-color-secondary, #9ca3af);
}

.spm-ds-child {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding-left: 1.25rem;
}

.spm-ds-child__icon {
  width: 16px;
  text-align: center;
  color: var(--text-color-secondary, #6b7280);
  font-size: 0.8rem;
}

.spm-ds-standalone {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.spm-ds-standalone__icon {
  width: 16px;
  text-align: center;
  color: var(--text-color-secondary, #6b7280);
  font-size: 0.8rem;
}

.spm-ds--inactive > span:not(.spm-ds-inactive) {
  color: var(--text-color-secondary, #9ca3af);
}

.spm-ds-inactive {
  font-size: 0.6875rem;
  font-style: italic;
  color: var(--text-color-secondary, #9ca3af);
}
</style>
